/**
 * Modal bloqueante que forca o usuario a cadastrar sua primeira passkey.
 *
 * Aparece quando GET /auth/passkey/needs-bootstrap devolve true — o que
 * acontece pra usuarios pre-existentes que ainda logaram por senha e nao
 * migraram pro fluxo de chave de acesso.
 *
 * Nao pode ser dispensado sem cadastrar (sem X, sem overlay-close, sem cancelar).
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useToast,
} from "@payflow/ui";
import {
  checkNeedsBootstrap,
  isPasskeySupported,
  registerPasskey,
  suggestPasskeyNickname,
} from "../services/webauthn.service";

export function PasskeyBootstrapPrompt() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [nickname, setNickname] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    checkNeedsBootstrap()
      .then((needs) => {
        if (cancelled) return;
        if (needs) {
          setNickname(suggestPasskeyNickname());
          setOpen(true);
        }
      })
      .catch(() => {
        // Falha no check nao deve bloquear o usuario — pode ser problema
        // temporario de rede. Ele tenta de novo no proximo reload.
      })
      .finally(() => {
        if (!cancelled) setChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nickname.trim()) {
      setError(t("passkeys.error_nickname_required"));
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await registerPasskey(nickname.trim());
      toast({ variant: "success", title: t("passkeys.add_success") });
      setOpen(false);
    } catch (err) {
      const code = err instanceof Error ? err.message : "";
      setError(mapError(code, t));
    } finally {
      setSubmitting(false);
    }
  };

  if (!checked) return null;

  const supported = isPasskeySupported();

  return (
    <Dialog
      open={open}
      // Ignora tentativas de fechar — usuario precisa concluir o cadastro.
      onOpenChange={() => {}}
    >
      <DialogContent size="md" hideCloseButton>
        <DialogHeader>
          <DialogTitle>{t("auth.passkey.bootstrap_title")}</DialogTitle>
        </DialogHeader>

        {!supported ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm">
              <p className="font-semibold text-warning">
                {t("auth.passkey.unsupported_title")}
              </p>
              <p className="mt-1 text-muted-foreground">
                {t("auth.passkey.bootstrap_unsupported_message")}
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("auth.passkey.bootstrap_message")}
            </p>
            <Input
              label={t("passkeys.nickname_label")}
              value={nickname}
              onChange={(e) => {
                setNickname(e.target.value);
                if (error) setError("");
              }}
              placeholder={t("passkeys.nickname_placeholder")}
              required
              autoFocus
            />
            {error && <p className="text-sm text-error">{error}</p>}
            <DialogFooter>
              <Button type="submit" loading={submitting} className="w-full">
                {t("auth.passkey.bootstrap_submit")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function mapError(code: string, t: (key: string) => string): string {
  switch (code) {
    case "PASSKEY_CANCELED":
      return t("auth.passkey.error_canceled");
    case "PASSKEY_UNSUPPORTED":
      return t("auth.passkey.error_unsupported");
    case "PASSKEY_ALREADY_REGISTERED":
      return t("auth.passkey.error_already_registered");
    default:
      return code || t("auth.passkey.error_generic");
  }
}
